import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Container,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useInView } from "react-intersection-observer";

const ANIMATION_DURATION = "0.8s";
const ANIMATION_TIMING_FUNCTION = "cubic-bezier(0.25, 0.1, 0.25, 1)";
const ANIMATION_INITIAL_TRANSFORM = "translateY(10vh)";
const HOVER_ANIMATION_DURATION = "0.3s";
const HOVER_ANIMATION_TIMING_FUNCTION = "ease-out";

const COLOR_PRIMARY_RED = "#E40614";
const COLOR_SECONDARY_BLUE = "#1D2D5C";
const COLOR_BACKGROUND_LIGHT = "#FEF9F9";
const COLOR_ABOUT_US_OVERLAY = "rgba(230, 0, 18, 0.05)";

const InfoSection = ({
  hexImage,
  label,
  title,
  description,
  indent,
  hexSize,
  skipInViewCheck,
}) => {
  const { ref, inView } = useInView({
    triggerOnce: false,
    threshold: 0.4,
    skip: skipInViewCheck,
  });

  const baseAnimatedStyle = {
    opacity: inView ? 1 : 0,
    transform: inView
      ? "translateY(0) scale(1)"
      : `${ANIMATION_INITIAL_TRANSFORM} scale(1)`,
    cursor: "default",
  };

  const getTransitionStyle = (delay = "0s") => ({
    transition: `opacity ${ANIMATION_DURATION} ${ANIMATION_TIMING_FUNCTION} ${inView ? delay : "0s"}, \
                 transform ${ANIMATION_DURATION} ${ANIMATION_TIMING_FUNCTION} ${inView ? delay : "0s"}`,
  });

  const getHoverTransformStyle = (scale) => ({
    transform: inView
      ? `translateY(0) scale(${scale})`
      : `${ANIMATION_INITIAL_TRANSFORM} scale(${scale})`,
    transition: `transform ${HOVER_ANIMATION_DURATION} ${HOVER_ANIMATION_TIMING_FUNCTION}`,
  });

  const imageStyles = {
    ...baseAnimatedStyle,
    position: "relative",
    width: hexSize,
    height: "auto",
    flexShrink: 0,
    ...getTransitionStyle("0.2s"),
    "&:hover": {
      ...getHoverTransformStyle(1.05),
    },
  };

  const titleStyles = {
    ...baseAnimatedStyle,
    color: COLOR_PRIMARY_RED,
    fontWeight: "bold",
    fontFamily: "Roboto, sans-serif",
    mb: { xs: 1, sm: 2 },
    fontSize: { xs: "0.9rem", sm: "1.2rem", md: "1.75rem" },
    whiteSpace: title.includes("\n") ? "pre-line" : "normal",
    display: "inline-block",
    ...getTransitionStyle("0.4s"),
    "&:hover": {
      ...getHoverTransformStyle(1.03),
    },
  };

  const descriptionStyles = {
    ...baseAnimatedStyle,
    color: COLOR_SECONDARY_BLUE,
    fontFamily: "Roboto, sans-serif",
    lineHeight: { xs: 1.5, sm: 1.7 },
    fontSize: { xs: "0.7rem", sm: "0.9rem", md: "1rem" },
    whiteSpace: description.includes("\n") ? "pre-line" : "normal",
    ...getTransitionStyle("0.6s"),
    "&:hover": {
      ...getHoverTransformStyle(1.02),
    },
  };

  const sectionContainerStyles = {
    display: "flex",
    alignItems: "flex-start",
    gap: { xs: 2, sm: 2, md: 3 },
    pl: { md: indent },
    mb: { xs: 4, sm: 8, md: 8 },
    mt: { xs: 4, sm: 0, md: 1 },
    height: { xs: "17vh", sm: "17vh" },
  };

  return (
    <Box ref={ref} sx={sectionContainerStyles}>
      <Box sx={imageStyles}>
        <Box
          component="img"
          src={hexImage}
          alt=""
          sx={{ display: "block", width: "100%", height: "auto" }}
        />
        <Typography
          component="span"
          sx={{
            position: "absolute",
            left: "48.5%",
            top: "45.5%",
            transform: "translate(-50%, -50%)",
            width: "75%",
            color: "#fff",
            fontFamily: "Roboto, sans-serif",
            fontWeight: "bold",
            fontSize: hexSize * 0.11,
            lineHeight: 1.2,
            textAlign: "center",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h2" component="h3" sx={titleStyles}>
          {title}
        </Typography>
        <Typography variant="body1" sx={descriptionStyles}>
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

InfoSection.propTypes = {
  hexImage: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  indent: PropTypes.string.isRequired,
  hexSize: PropTypes.number.isRequired,
  skipInViewCheck: PropTypes.bool,
};

InfoSection.defaultProps = {
  skipInViewCheck: false,
};

const BackgroundImage = ({ src, alt, sxProps }) => (
  <Box
    component="img"
    src={src}
    alt={alt}
    sx={{
      position: "absolute",
      zIndex: 0,
      userSelect: "none",
      pointerEvents: "none",
      ...sxProps,
    }}
  />
);

BackgroundImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  sxProps: PropTypes.object,
};

BackgroundImage.defaultProps = {
  sxProps: {},
};

const SECTIONS_DATA = [
  {
    id: "mission",
    label: "MISSION",
    hexImage: "about/mission.png",
    title: "人をつなぎ、新たな価値を創出する会社",
    description:
      "顧客の課題は「伝えるツール」だけでは解決しません。誰に、どう伝えるかが重要です。\n私たちは、課題を明確して細部まで捉え、解決に必要なキーパーソンをつなぎ、\n利益を最大化するためのストーリーを設計・ご提案します。",
    indent: "0px",
  },
  {
    id: "vision",
    label: "VISION",
    hexImage: "about/vision.png",
    title: "事業会社がプロダクト磨きに専念できる\n環境を、私たちの手で",
    description:
      "私たちは引き立て役です。顧客が磨き上げたプロダクトを、いかに魅力的に、わかりやすく、興味関心を醸成\nするか日々研究しながら、既存の手法にとらわれないやり方でプロデュースし、安心してお任せいただけるア\nウトプットを提供します。",
    indent: "60px",
  },
  {
    id: "value",
    label: "VALUE",
    hexImage: "about/value.png",
    title: "リピート率95%以上の信頼",
    description:
      "おかげさまで、これまでお取引いただいた顧客のリピート率（※）は95%以上。仕事の報酬は仕事と\n信じ、信頼関係構築に尽力してきました。伝わるコミュニケーションの媒介者としてこれからも信用\nに応えぬよう仕事でお応えしていきます。",
    indent: "0px",
  },
];

const BACKGROUND_IMAGES_DATA = [
  {
    id: "bg-top-left",
    src: "about/shape-top-left.png",
    alt: "左上の装飾図形",
    sxProps: {
      top: { xs: 20, sm: 20, md: 96 },
      left: { xs: -20, sm: -10, md: 0 },
      width: { xs: 150, sm: 180, md: 350 },
    },
  },
  {
    id: "bg-top-right",
    src: "about/shape-top-right.png",
    alt: "右上の装飾図形",
    sxProps: {
      top: { xs: -50, sm: -60, md: -80 },
      right: { xs: -20, sm: -10, md: 0 },
      width: { xs: 150, sm: 180, md: 350 },
    },
  },
  {
    id: "bg-bottom-right",
    src: "about/shape-bottom-right.png",
    alt: "右下の装飾図形",
    sxProps: {
      bottom: { xs: 80, sm: 200, md: 110 },
      right: { xs: 5, sm: 5, md: 0 },
      width: { xs: 220, sm: 300, md: 500 },
    },
  },
];

const SCROLL_TRIGGER_POINTS_RATIO = [0.05, 0.3, 0.55, 0.8];
const AboutUsSection = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const hexSize = isSmallScreen ? 100 : isMediumScreen ? 140 : 170;

  const [visibleSectionIndex, setVisibleSectionIndex] = useState(-1);
  const [headingScrollOffset, setHeadingScrollOffset] = useState(0);
  const headingScrollDistance = Number.parseFloat(
    theme.spacing(isSmallScreen ? 11 : isMediumScreen ? 16 : 32),
  );

  const scrollContainerRef = useRef(null);
  const stickyContentRef = useRef(null);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || !stickyContentRef.current) return;

    const scrollTop = window.scrollY - scrollContainerRef.current.offsetTop;
    const stickyHeight = stickyContentRef.current.offsetHeight;
    setHeadingScrollOffset(
      Math.min(Math.max(scrollTop, 0), headingScrollDistance),
    );

    const triggers = SCROLL_TRIGGER_POINTS_RATIO.map(
      (ratio) => ratio * stickyHeight,
    );

    let newIndex = -1;
    if (scrollTop >= triggers[3]) newIndex = 3;
    else if (scrollTop >= triggers[2]) newIndex = 2;
    else if (scrollTop >= triggers[1]) newIndex = 1;
    else if (scrollTop >= triggers[0]) newIndex = 0;

    if (newIndex !== visibleSectionIndex) {
      setVisibleSectionIndex(newIndex);
    }
  }, [visibleSectionIndex, headingScrollDistance]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const processedSections = SECTIONS_DATA.map((section) =>
    isSmallScreen
      ? {
          ...section,
          title: section.title.replace(/\n/g, " "),
          description: section.description.replace(/\n/g, " "),
          indent: "0px",
        }
      : section,
  );

  return (
    <Box
      id="about-container"
      ref={scrollContainerRef}
      sx={{
        position: "relative",
        height: { xs: "200vh", sm: "180vh", md: "180vh", lg: "180vh" },
        mb: { xs: 5, sm: 0, md: 12 },
        pb: { xs: 5, sm: 0, md: 15 },
      }}
    >
      <Box
        id="about"
        ref={stickyContentRef}
        sx={{
          backgroundColor: COLOR_BACKGROUND_LIGHT,
          position: "sticky",
          top: 0,
          height: { xs: "5vh", sm: "0vh", md: "90vh" },
          minHeight: { xs: "780px", sm: "920px", md: "900px", lg: "900px" },
          overflow: "hidden",
          py: { xs: 3, sm: 5, md: 8 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {BACKGROUND_IMAGES_DATA.map((img) => (
          <BackgroundImage
            key={img.id}
            src={img.src}
            alt={img.alt}
            sxProps={img.sxProps}
          />
        ))}

        <Container
          maxWidth="lg"
          sx={{
            position: "relative",
            zIndex: 1,
            pt: {
              xs: `calc(${theme.spacing(17)} - ${headingScrollOffset}px)`,
              sm: `calc(${theme.spacing(18)} - ${headingScrollOffset}px)`,
              md: `calc(${theme.spacing(37)} - ${headingScrollOffset}px)`,
            },
            pb: { xs: 5, sm: 2, md: 0 },
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              mb: { xs: 2.5, sm: 4, md: 10 },
              position: "relative",
            }}
          >
            <Typography
              variant="h1"
              component="div"
              aria-hidden="true"
              sx={{
                position: "absolute",
                top: { xs: "-2vh", sm: "-3vh", md: "-7vh" },
                left: "50%",
                transform: "translateX(-50%)",
                fontSize: { xs: "64px", sm: "6rem", md: "11rem" },
                fontWeight: "bold",
                fontFamily: "Roboto, sans-serif",
                color: COLOR_ABOUT_US_OVERLAY,
                zIndex: 0,
                userSelect: "none",
                pointerEvents: "none",
                whiteSpace: "nowrap",
                width: "100%",
              }}
            >
              About Us
            </Typography>
            <Box sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  color: COLOR_PRIMARY_RED,
                  display: "inline-block",
                  mb: 0.5,
                  mt: { xs: 1, sm: 2, md: 3 },
                  fontWeight: "bold",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: { xs: "16px", sm: "1.8rem", md: "2.25rem" },
                }}
              >
                私たちについて
              </Typography>
              <Box
                sx={{
                  height: 2,
                  width: "6%",
                  minWidth: "50px",
                  bgcolor: COLOR_SECONDARY_BLUE,
                  mx: "auto",
                }}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
            marginBottom={"10vh"}
          >
            {processedSections.map((section, index) =>
              index <= visibleSectionIndex && index <= 2 ? (
                <InfoSection
                  key={`${section.id}-${index}`}
                  hexImage={section.hexImage}
                  label={section.label}
                  title={section.title}
                  description={section.description}
                  indent={section.indent}
                  hexSize={hexSize}
                  skipInViewCheck={false}
                />
              ) : null,
            )}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutUsSection;
